import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';
import CtaBanner from '../components/CtaBanner';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const services = [
  {
    title: 'Digital Roadmap',
    body: 'A practical systems blueprint that identifies operational bottlenecks, maps automation opportunities, and defines the infrastructure needed to scale reliably.',
    deliverables: [
      'Current-state workflow audit + systems architecture map',
      'Automation opportunities & risk/failure analysis',
      '30/60/90-day implementation roadmap with tooling recommendations',
    ],
  },
  {
    title: 'Workflow Automation',
    body: 'Operational processes engineered into reliable systems. Document handling, intake, follow-up, internal routing — the work your firm does every day, structured and observable.',
    deliverables: [
      'Process map & failure register',
      'Idempotent execution',
      'Audit log + telemetry',
    ],
  },
  {
    title: 'AI Agents',
    body: 'Task-scoped agents with tools, guardrails, and evaluation harnesses. Built around your firm\'s decision rules — not a generic chatbot wrapped in a brand colour.',
    deliverables: [
      'Tool use & guardrails',
      'Evaluation harness',
      'Human-in-the-loop hooks',
    ],
  },
  {
    title: 'Integration',
    body: 'Connecting the systems already running your firm. CRM, document store, calendar, billing, comms. One spine. Documented contracts. No spreadsheet glue.',
    deliverables: [
      'API contracts documented',
      'Identity & secret rotation',
      'Replay & reconciliation',
    ],
  },
  {
    title: 'Operational AI',
    body: 'Embedded systems for partners and operations leads. Recurring reports, anomaly alerts, KPI surfaces — built once, run forever, monitored always.',
    deliverables: [
      'Scheduled jobs & triggers',
      'Alerting & runbooks',
      'Quarterly review loop',
    ],
  },
];

export default function Services() {
  useDocumentMeta({
    title: 'Services — Samia Systems',
    description:
      'Workflow automation, AI agents, integration, and operational AI for professional services firms. Engineered, tested, deployed.',
  });

  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scope = pageRef.current;
    if (!scope) return;
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        ['.svc-label', '.svc-h1', '.svc-p'],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.13, ease: 'power2.out', delay: 0.1 }
      );

      // Cards stagger in on scroll
      ScrollTrigger.batch('.svc-card', {
        start: 'top 88%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 44 },
            { opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power2.out' }
          ),
      });
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef}>
      <section className="grain relative bg-black px-6 pb-20 pt-40">
        <div className="container-x">
          <p className="svc-label label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Services
          </p>
          <h1 className="svc-h1 max-w-4xl font-display text-hero font-light text-white text-balance">
            What we build, and how it is delivered.
          </h1>
          <p className="svc-p mt-8 max-w-2xl font-sans text-base text-white/85">
            Engineering-grade AI automation for professional services firms.
            Every service category enters via a Technical Blueprint and exits
            with monitored, documented, production code.
          </p>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x grid gap-6 md:grid-cols-2">
          {services.map((s, i) => (
            <article
              key={s.title}
              className={`svc-card grain rounded-4xl border border-white/10 bg-charcoal p-9 ${
                i === services.length - 1 && services.length % 2 === 1
                  ? 'md:col-span-2 md:mx-auto md:w-[calc(50%-12px)]'
                  : ''
              }`}
            >
              <h2 className="font-display text-3xl font-light text-white">
                {s.title}
              </h2>
              <p className="mt-4 font-sans text-body text-white/85">{s.body}</p>
              <ul className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6">
                {s.deliverables.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-3 font-sans text-[0.78rem] uppercase tracking-label text-white/85"
                  >
                    <span className="mt-1 inline-block h-px w-4 bg-gold" />
                    {d}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <CtaBanner
        heading="Not sure which fits? That's what the Diagnostic is for."
        sub="Thirty minutes. We map your process, identify the bottleneck, and tell you exactly what to build — and what not to."
        primaryLabel="Book a Diagnostic"
        primaryTo="/book"
        secondaryLabel="Browse Library"
        secondaryTo="/library"
      />
    </div>
  );
}
